<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>
		<breadcrumbs
			:breadcrumbs="page.breadcrumbs"
			:class="page.headingImage ? '' : 'no-slider'"
			:home="defaults[locale].website.hotelName"
		/>
		<main-content :title="page.title || page.header" :content="page.content" :booking-id="page.bookingID" />

		<section v-for="(room, index) in roomsData" :key="room.pageID" class="rooms-items">
			<div class="row">
				<div
					class="columns column4 left bg-image-content"
					:class="{ left: index % 2 == 0, right: index % 2 != 0 }"
				>
					<h2>{{ room.title || room.header }}</h2>
					<div v-parse-links v-html="room.content" />
					<div class="room-icons">
						<div v-if="room.pax" class="room-icon">
							<font-awesome-icon :icon="`fal fa-users`" size="1x" />
							<span>{{ room.pax }}</span>
						</div>
						<div v-if="room.surface" class="room-icon">
							<font-awesome-icon :icon="`fal fa-ruler-combined`" size="1x" />
							<span>{{ room.surface }}</span>
						</div>
					</div>
					<nuxt-link :to="room.filename" class="button button-no-fill">
						{{ $t('viewThisRoom') }}
					</nuxt-link>
					<p v-if="room.bookingID">
						<book-button :roomtypeid="parseInt(room.bookingID, 10)" class="button button-cta">
							{{ $t('bookThisRoom') }}
						</book-button>
					</p>
				</div>
			</div>

			<client-only>
				<carousel
					:items-to-show="1"
					:wrap-around="true"
					:pagination-enabled="false"
					:autoplay="false"
					:pause-autoplay-on-hover="false"
					:transition="1500"
					class="slides"
				>
					<slide v-for="image in room.images" :key="image.image">
						<div class="slide">
							<picture>
								<source
									v-if="image.imagesWebp"
									:srcset="image.imagesWebp[1]"
									media="(max-width: 500px)"
									type="image/webp"
								/>
								<source v-if="image.imagesWebp" :srcset="image.imagesWebp[0]" type="image/webp" />
								<source :srcset="image.images[1]" media="(max-width: 500px)" />
								<source :srcset="image.images[0]" />
								<img :src="image.image" :alt="image.imageAlt" loading="lazy" />
							</picture>
						</div>
					</slide>
				</carousel>
			</client-only>
		</section>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();

await fetchPage();
await fetchDefaults();

const { data: roomsData } = await useWebsiteFetch('rooms', {
	query: { language: locale.value },
	key: `${locale.value}/rooms`,
});
</script>

<style lang="scss" scoped>
.rooms-items {
	margin-bottom: 5px;
	padding: 200px 0;
}

.bg-image-content {
	background: $contentblock-background-color;
	padding: 50px;
	z-index: 3;
}

.room-icons {
	display: flex;
	flex-flow: wrap row;
	justify-content: flex-start;
	align-items: center;
	gap: 10px;
	margin: 0 0 20px;

	.room-icon {
		display: flex;
		gap: 4px;
		margin: 0 20px 0 0;
	}

	span {
		font-size: 12px;
	}

	svg {
		color: $cta-color;
	}
}

.slides {
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;

	img {
		height: 100% !important;
		width: 100%;
		object-fit: cover;
	}

	li {
		position: relative;
		width: 100%;
		height: 100%;

		&::before {
			content: '';
			position: absolute;
			inset: 0;
			background: rgb(0 0 0 / 10%);
			z-index: 2;
		}
	}
}

.slides .slide {
	width: 100%;
}

@media (max-width: 800px) {
	.rooms-items {
		margin-bottom: 40px;
		position: relative;
		padding: 0;
		display: flex;
		flex-flow: column-reverse;
		justify-content: center;
		align-items: center;

		.slides {
			position: relative;
			margin: 0 20px;
			width: calc(100% - 40px);
		}
	}

	.bg-image-content {
		padding: 30px;
		width: 100%;
		font-size: 15px;
	}
}
</style>
